/**
 * AppProviders - Centralized provider setup for DramaDetector
 *
 * Extracted from _layout.tsx to reduce complexity and improve maintainability.
 * Contains all app-level providers in correct order:
 * - KeyboardProvider (react-native-keyboard-controller)
 * - I18nextProvider (internationalization)
 * - PersistQueryClientProvider (React Query with offline persistence)
 * - AlertProvider (global alert system)
 * - AuthProvider (authentication context)
 * - SafetyModalsProvider (ToS, Crisis Resources, Concern Flag modals)
 * - TierStyleProvider (tier-based styling and upgrade animations)
 */

import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { I18nextProvider, i18n } from "@dramadetector/shared";
import {
  createQueryClient,
  createQueryPersister,
  dehydrateOptions,
} from "@dramadetector/api-client";
import { AuthProvider } from "../contexts/AuthContext";
import { AlertProvider } from "../contexts/AlertProvider";
import { TierStyleProvider } from "./TierStyleProvider";
import { SafetyModalsProvider } from "../contexts/SafetyModalsContext";

// Create React Query client and persister (moved from _layout.tsx)
const queryClient = createQueryClient();
const persister = createQueryPersister();

interface AppProvidersProps {
  children: React.ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <KeyboardProvider>
      <I18nextProvider i18n={i18n}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24 * 7, // 7 days max cache age
            dehydrateOptions,
            // FUTURE: Uncomment when app versioning is implemented
            // This will auto-invalidate cache on version bumps
            // buster: Constants.expoConfig?.version || '1.0.0',
          }}
        >
          <AlertProvider>
            <AuthProvider>
              {/* SafetyModalsProvider manages ToS, Crisis Resources, and Concern Flag modals
                  Priority: ToS (blocks all) > Crisis Resources (auto-trigger) > Concern Flag */}
              <SafetyModalsProvider>
                {/* TierProvider removed - useTier hook from @/hooks/useTier now uses
                    React Query internally and doesn't require a Context wrapper.
                    TierStyleProvider still needed for web data-tier attribute and upgrade animations. */}
                <TierStyleProvider>
                  {children}
                </TierStyleProvider>
              </SafetyModalsProvider>
            </AuthProvider>
          </AlertProvider>
        </PersistQueryClientProvider>
      </I18nextProvider>
    </KeyboardProvider>
  );
}
